import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';

import { createEffectFactory } from '@core/ngrx-legacy';

import { ViewActions } from './view.store';
import { ViewsHttpService } from '../../services/views-http.service';

@Injectable({ providedIn: 'root' })
export class ViewEffects {
  private readonly actions$ = inject(Actions);
  private readonly viewsHttpService = inject(ViewsHttpService);

  public effect = createEffectFactory(ViewActions, this.actions$);

  public fetchOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewActions.fetchOne),
      mergeMap(({ payload: { dataSourceId, viewId } }) =>
        this.viewsHttpService.fetchView(dataSourceId, viewId).pipe(
          map(data => ViewActions.fetchOneSuccess({ payload: { data } })),
          catchError((error: HttpErrorResponse) =>
            of(ViewActions.fetchOneFailed({ payload: { error, id: viewId } })),
          ),
        ),
      ),
    ),
  );

  public createOne$ = this.effect(
    ViewActions.createOnePessimistic,
    ({ payload: { dataSourceId, data } }) =>
      this.viewsHttpService.createView(dataSourceId, data).pipe(map(data => ({ data }))),
  );

  public duplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewActions.duplicate),
      switchMap(({ payload: { dataSourceId, data, correlationId } }) =>
        this.viewsHttpService.createView(dataSourceId, data).pipe(
          map(data =>
            ViewActions.duplicateSuccess({
              payload: { data, correlationId },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(ViewActions.duplicateFailed({ payload: { error, correlationId } })),
          ),
        ),
      ),
    ),
  );

  public removeOne$ = this.effect(
    ViewActions.removeOnePessimistic,
    ({ payload: { dataSourceId, viewId } }) =>
      this.viewsHttpService.removeView(dataSourceId, viewId).pipe(map(data => ({ data }))),
  );

  public updateOne$ = this.effect(
    ViewActions.updateOnePessimistic,
    ({ payload: { dataSourceId, data } }) =>
      this.viewsHttpService.updateView(dataSourceId, data).pipe(map(data => ({ data }))),
  );

  public saveSnapshot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewActions.saveSnapshot),
      switchMap(({ payload: { dataSourceId, viewId, codename, form } }) =>
        this.viewsHttpService.saveSnapshot(dataSourceId, viewId, codename, form).pipe(
          map(data => ViewActions.saveSnapshotSuccess({ payload: { dataSourceId, viewId } })),
          catchError((error: HttpErrorResponse) =>
            of(
              ViewActions.saveSnapshotFailed({
                payload: { dataSourceId, error, viewId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public clearSnapshot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewActions.clearSnapshot),
      switchMap(({ payload: { dataSourceId, viewId, codename } }) =>
        this.viewsHttpService.clearSnapshot(dataSourceId, viewId, codename).pipe(
          map(data => ViewActions.clearSnapshotSuccess({ payload: { dataSourceId, viewId } })),
          catchError((error: HttpErrorResponse) =>
            of(
              ViewActions.clearSnapshotFailed({
                payload: { dataSourceId, error, viewId },
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
